import { PowerBIEmbed } from "powerbi-client-react";
import * as powerbi from "powerbi-client";
import styled from "styled-components/macro";
import { useEffect, useState } from "react";
import { getTokenInfo } from "contexts/AuthManager/Auth";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import useQueryString from "utils/customHooks/useQueryString";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { SHINY_ANIMATED, useBreakpoints } from "constants/constants";
import ReactGA from "react-ga4";
import { lightestGrey } from "constants/colors";
import { useHideNav, useRavenKiosk } from "utils/customHooks/genericHooks";
import _, { isNil } from "lodash";

/** simple form page that lets you download Timeline images by specifying processes and time range to view */
function AnalyticsPowerBIPage() {
  const ckey = useCustomerKey();
  const ravenKiosk = useRavenKiosk();
  const hideNav = useHideNav();

  const [powerBiResponse, setPowerBiResponse] = useState<{
    embed_token: string | null;
    embed_url: string | null;
  }>({
    embed_token: null,
    embed_url: null,
  });

  const [powerBiReportPages, setPowerBiReportPages] = useState<
    { display_name: string; name: string }[] | undefined
  >(undefined);

  const [
    {
      general: { powerBiReportId },
    },
  ] = useFlightConfig();

  useEffect(() => {
    if (!powerBiReportId || !ckey) {
      return;
    }
    const apiUrl = `https://${ckey}.gke-us-east1.raven-cloud.com/askr`;
    const powerBiApiUrl = `${apiUrl}/api/powerBIEmbedToken?reportId=${powerBiReportId}`;
    const powerBiReportPagesUrl = `${apiUrl}/api/powerBIReportPages?reportId=${powerBiReportId}`;
    const variables = {
      headers: {
        Authorization: `Bearer ${getTokenInfo().idToken}`,
      },
    };

    fetch(powerBiApiUrl, variables)
      .then(response => response.json())
      .then(data => {
        if (data.data) {
          return setPowerBiResponse(data.data);
        }
      })
      .catch(error => console.error(error));

    fetch(powerBiReportPagesUrl, variables)
      .then(response => {
        if (!response.ok) {
          setPowerBiReportPages([]);
          throw new Error(`Failed to fetch report pages: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.data) {
          return setPowerBiReportPages(data.data);
        }
      })
      .catch(error => console.error(error));
  }, [ckey, powerBiReportId]);

  const loading =
    !powerBiResponse.embed_token ||
    !powerBiReportId ||
    powerBiReportPages === undefined;
  return (
    <Styles $hideNav={hideNav}>
      {loading ? (
        <div className="spinner-container">
          <Spinner />
        </div>
      ) : (
        <div className="report-container">
          <PowerBIReport
            reportId={powerBiReportId}
            accessToken={powerBiResponse.embed_token}
            embedUrl={powerBiResponse.embed_url}
            reportPages={powerBiReportPages}
            ravenKiosk={ravenKiosk}
          />
        </div>
      )}
    </Styles>
  );
}

const Styles = styled.div<{ $hideNav: boolean }>`
  display: flex;
  width: 100vw;
  gap: 1em;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
  background: white;
  min-height: 50vh;
  height: calc(100vh - ${props => (props.$hideNav ? 0 : 66)}px);
  .spinner-container {
    margin-top: 2em;
  }
  .report-container {
    width: 100vw;
    height: 100%;
    .reportClass {
      height: 100%;
    }
  }
`;
export default AnalyticsPowerBIPage;

const sanitizePageDisplayName = (pageName: string) => {
  return pageName.toLowerCase().replaceAll(" ", "_");
};

const PowerBIReport = ({
  reportId,
  accessToken,
  embedUrl,
  reportPages,
  ravenKiosk,
}) => {
  const { isTabletOrLarger } = useBreakpoints();
  const analyticsPageQueryString =
    useQueryString().analyticsPage?.toString() || undefined;
  const [powerBIEmbedLoading, setPowerBIEmbedLoading] = useState(true);

  const sanitizedAnalyticsPageQueryString = analyticsPageQueryString
    ? sanitizePageDisplayName(analyticsPageQueryString)
    : analyticsPageQueryString;

  const pageName =
    analyticsPageQueryString && reportPages && reportPages.length > 0
      ? reportPages.find(page => {
          return (
            sanitizePageDisplayName(page.display_name) ===
              sanitizedAnalyticsPageQueryString ||
            // page.name is an alphanumeric ID, so it may be case sensitive
            page.name === analyticsPageQueryString
          );
        })?.name
      : undefined;

  const defaultEmbedConfigSettings = {
    panes: {
      filters: {
        visible: false,
      },
    },
    background: powerbi.models.BackgroundType.Transparent,
    layoutType: isTabletOrLarger
      ? powerbi.models.LayoutType.Master
      : powerbi.models.LayoutType.MobilePortrait,
  };

  const ravenKioskPowerBIEmbedConfigSettings = ravenKiosk
    ? {
        panes: {
          pageNavigation: {
            visible: isNil(pageName),
          },
        },
        zoomLevel: powerbi.models.DisplayOption.FitToPage,
        layoutType: powerbi.models.LayoutType.Master,
      }
    : {};

  const embedConfigSettings = _.merge(
    defaultEmbedConfigSettings,
    ravenKioskPowerBIEmbedConfigSettings
  );

  return (
    <>
      {
        // There's occasionally a noticeable delay between when AnalyticsPowerBIPage hides its Spinner + starts rendering the PowerBIEmbed
        // component, and the embedded page showing its own loading indicator, so we can re-show a Spinner until the "Loaded"
        // event is received.
        powerBIEmbedLoading && (
          <SkeletonLoaderPowerBIStyles>
            {/* left control panel */}
            <div className="grid-item"></div>
            {/* 4 grid squares to the right */}
            <div className="grid-item"></div>
            <div className="grid-item"></div>
            <div className="grid-item"></div>
            <div className="grid-item"></div>
          </SkeletonLoaderPowerBIStyles>
        )
      }
      <PowerBIEmbed
        key={isTabletOrLarger ? "desktop" : "mobile"}
        embedConfig={{
          type: "report",
          id: reportId,
          embedUrl,
          accessToken,
          pageName,
          tokenType: powerbi.models.TokenType.Embed,
          settings: embedConfigSettings,
        }}
        eventHandlers={
          new Map([
            [
              "loaded",
              () => {
                setPowerBIEmbedLoading(false);
                console.log("Report loaded");
              },
            ],
            ["rendered", () => console.log("Report rendered")],
            ["error", event => console.log(event?.detail)],
            [
              "pageChanged",
              e => {
                const pageChangedTo = e?.detail.newPage.displayName;
                console.log("Page changed:", e, pageChangedTo);
                ReactGA.event({
                  category: "User",
                  action: `Changed Sight Analytics Page to ${pageChangedTo}`,
                });
              },
            ],
          ])
        }
        cssClassName="reportClass"
        getEmbeddedComponent={embeddedReport => {
          // You can access the embedded report instance here
          // this.report = embeddedReport as Report;
        }}
        // style={{ height: '600px' }}
      />
    </>
  );
};

const SkeletonLoaderPowerBIStyles = styled.div`
  /* a grid of 
  - 1 full-height narrow column on the left, 
  - to the right of which are 4 squares
  */
  .grid-item {
    background: ${lightestGrey};
    ${SHINY_ANIMATED};
  }
  /* the first grid-item has a 2-row span */
  .grid-item:first-child {
    grid-row: 1 / 3;
  }
  display: grid;

  grid-template-columns: 0.3fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;

  gap: 1em;
  width: 100vw;
  height: 100%;
`;
